import React, { useState, useContext } from "react";
import AuthContext from "../../contexts/AuthContext";
import { Link } from 'react-router-dom';
import { MODES } from "../configuratorConstants";
import {
  PlusCircleIcon, ChevronDownIcon, ChevronUpIcon,
  TrashIcon, PlusIcon, UserCircleIcon,
  ArrowRightStartOnRectangleIcon, ArrowLeftOnRectangleIcon,
  PlayIcon
} from "@heroicons/react/24/outline";
import { SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const ConfiguratorToolbar = ({
  activeMode,
  setActiveMode,
  onClearAll,
  floors,
  currentFloorId,
  addFloor,
  deleteFloor,
  switchToFloor,
  onOpenEnquiryModal,
  isAiPanelOpen,
  setIsAiPanelOpen,
  aiQuery,
  setAiQuery,
  handleAiSubmit,
  isAiLoading,
  totalCost,
  onOpenInstructionModal,
}) => {
  const { user, logout } = useContext(AuthContext);
  const { t } = useTranslation();
  const modsArray = [
    { key: MODES.MODULAR, label: t('toolbar.modular_mode') },
    { key: MODES.FRAME, label: t('toolbar.frame_mode') },
    { key: MODES.FRAMELESS, label: t('toolbar.frameless_mode') },
  ];
  const [isFloorDropdownOpen, setIsFloorDropdownOpen] = useState(false);
  const currentFloor = floors.find(f => f.id === currentFloorId);
  const isAdmin = user?.role === 'admin';

  const renderUserBlock = () => {
    if (!user) return null;

    // Форматируем цену
    const formatPrice = (price) => {
      return new Intl.NumberFormat('uk-UA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price);
    };

    return (
      <div className="flex items-center space-x-2">
        {totalCost !== undefined && totalCost !== null && (
          <div className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-800 text-sm rounded-lg font-semibold shadow-sm">
            <span className="text-gray-600 mr-1">{t('toolbar.current_price', 'Поточна ціна')}:</span>
            <span className="text-primary-blue">{formatPrice(totalCost)} грн</span>
          </div>
        )}

        {isAdmin ? (
          <Link to="/admin" title={t('toolbar.admin_panel_title', { email: user.email })}>
            <UserCircleIcon className="w-8 h-8 text-yellow-500 hover:text-yellow-600 cursor-pointer transition-colors" />
          </Link>
        ) : (
          <UserCircleIcon className="w-8 h-8 text-gray-500" title={user.email} />
        )}

        <button
          onClick={logout}
          className="p-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors duration-150"
          title={t('toolbar.logout')}
        >
          <ArrowLeftOnRectangleIcon className="w-5 h-5" />
        </button>
      </div>
    );
  };

  return (
    <div className="p-2.5 bg-card-bg-light text-gray-800 border-b border-gray-300/80 flex justify-between items-center flex-shrink-0 shadow-lg h-16 relative z-10">
      <div className="flex items-center flex-shrink-0">
        <div className="mr-4 sm:mr-6 text-2xl font-extrabold tracking-wider text-primary-blue uppercase select-none">
          Конфігуратор
        </div>
        <div className="hidden sm:flex items-center space-x-1 bg-gray-200 p-0.5 rounded-md">
          {modsArray.map((mode) => {
            const isFrameMode = mode.key === MODES.FRAME;
            const isDisabled = isFrameMode && !isAdmin; // Каркасний режим доступний тільки для адміністраторів
            return (
              <button
                key={mode.key}
                onClick={() => !isDisabled && setActiveMode(mode.key)}
                disabled={isDisabled}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors duration-150
                  ${isDisabled ? "opacity-40 cursor-not-allowed bg-gray-100" : ""}
                  ${activeMode === mode.key ? "bg-primary-blue text-white shadow-sm" : "text-gray-700 hover:bg-gray-300 hover:text-gray-900"}
                  ${isDisabled ? "hover:bg-gray-100 hover:text-gray-500" : ""}`}
                title={isDisabled ? "Цей режим тимчасово недоступний" : ""}
              >
                {mode.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="hidden sm:flex flex-grow justify-center items-center space-x-2 mx-4">
        {(activeMode === MODES.MODULAR || activeMode === MODES.FRAMELESS || activeMode === MODES.FRAME) && (
          <>
            {activeMode === MODES.MODULAR && (
              <div className="relative">
                <button
                  onClick={() => setIsFloorDropdownOpen(!isFloorDropdownOpen)}
                  className="flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-md font-medium transition-colors duration-150 border border-gray-300"
                >
                  {currentFloor?.name || t('toolbar.floors')}
                  {isFloorDropdownOpen ? <ChevronUpIcon className="w-4 h-4 ml-1.5" /> : <ChevronDownIcon className="w-4 h-4 ml-1.5" />}
                </button>
                {isFloorDropdownOpen && (
                  <div className="absolute top-full mt-1.5 left-0 w-56 bg-white border border-gray-300 rounded-lg shadow-xl z-20 py-1.5">
                    {floors.map((floor) => (
                      <div key={floor.id} className="flex items-center justify-between mx-1.5 rounded-md hover:bg-gray-100 transition-colors duration-100">
                        <button
                          onClick={() => { switchToFloor(floor.id); setIsFloorDropdownOpen(false); }}
                          className={`text-left w-full px-3 py-2 text-sm ${floor.id === currentFloorId ? "text-primary-blue font-semibold" : "text-gray-700"}`}
                        >
                          {floor.name}
                        </button>
                        {floors.length > 1 && (
                          <button onClick={() => deleteFloor(floor.id)} title={t('toolbar.delete_floor', { floorName: floor.name })} className="ml-2 mr-2 p-1 text-red-500 hover:text-red-600 rounded-md hover:bg-red-100">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="border-t border-gray-300/70 my-1.5 mx-1.5"></div>
                    <button
                      onClick={() => { addFloor(); setIsFloorDropdownOpen(false); }}
                      disabled={floors.length >= 3}
                      className={`w-[calc(100%-0.75rem)] mx-1.5 flex items-center text-left px-3 py-2 text-sm rounded-md transition-colors duration-100 ${floors.length >= 3
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-green-600 hover:bg-green-100 hover:text-green-700'
                        }`}
                    >
                      <PlusIcon className="w-4 h-4 mr-2" /> {t('toolbar.add_floor')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={onClearAll}
              className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md font-medium transition-colors duration-150 shadow-sm"
              title={t('toolbar.delete_all_title')}
            >
              <TrashIcon className="w-5 h-5 mr-1.5" />
              {t('toolbar.delete_all')}
            </button>
          </>
        )}
      </div>

      <div className="flex items-center flex-shrink-0 space-x-3">
        {/* Кнопка відеоінструкцій */}
        <button
          onClick={onOpenInstructionModal}
          className="flex items-center px-4 py-2 bg-white hover:bg-gray-100 text-primary-blue text-sm rounded-lg font-semibold transition-colors duration-150 shadow-md border border-gray-300"
          title={t('instructions.button')}
        >
          <PlayIcon className="w-5 h-5 mr-2" />
          <span>{t('instructions.button')}</span>
        </button>

        {/* Блок кнопки "Розрахувати вартість" */}
        <div className="flex items-center">
          <button
            onClick={onOpenEnquiryModal}
            className="flex items-center px-4 py-2 bg-primary-blue hover:bg-hover-blue text-white text-sm rounded-lg font-semibold transition-colors duration-150 shadow-md"
            title={t('toolbar.calculate')}
          >
            <ArrowRightStartOnRectangleIcon className="w-5 h-5 mr-2" />
            <span>{t('toolbar.calculate')}</span>
          </button>
        </div>

        {/* Кнопка AI Асистент */}
        {activeMode === MODES.MODULAR && (
          <button
            onClick={() => setIsAiPanelOpen(true)}
            className="flex items-center px-4 py-2 bg-primary-blue hover:bg-hover-blue text-white text-sm rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <SparklesIcon className="w-5 h-5 mr-2" />
            AI Асистент
          </button>
        )}

        {/* Модальна панель AI Асистента */}
        <AnimatePresence>
          {isAiPanelOpen && (
            <>
              {/* Затемнення фону */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 bg-black/20 z-40"
                onClick={() => setIsAiPanelOpen(false)}
              />

              {/* Панель */}
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[480px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
              >
                {/* Заголовок */}
                <div className="flex items-center justify-between px-5 py-4 bg-primary-blue">
                  <div className="flex items-center text-white">
                    <SparklesIcon className="w-6 h-6 mr-3" />
                    <div>
                      <h3 className="font-semibold text-lg">AI Асистент</h3>
                      <p className="text-xs text-white/80">Опишіть що хочете створити</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsAiPanelOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Контент */}
                <div className="p-5">
                  <textarea
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && aiQuery.trim() && !isAiLoading) {
                        e.preventDefault();
                        handleAiSubmit();
                      }
                    }}
                    placeholder="Наприклад:&#10;• Створи спальню з ванною кімнатою&#10;• Додай кухню та вітальню&#10;• Зроби двокімнатну квартиру"
                    className="w-full h-32 px-4 py-3 text-sm text-gray-700 placeholder-gray-400 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-blue/50 focus:border-primary-blue transition-all resize-none"
                    disabled={isAiLoading}
                    autoFocus
                  />

                  {/* Підказки */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {['Спальня + ванна', 'Кухня-студія', 'Двокімнатна'].map((hint) => (
                      <button
                        key={hint}
                        onClick={() => setAiQuery(hint)}
                        className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-primary-blue/10 hover:text-primary-blue text-gray-600 rounded-full transition-colors"
                        disabled={isAiLoading}
                      >
                        {hint}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Футер з кнопкою */}
                <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Enter для відправки
                  </p>
                  <button
                    onClick={handleAiSubmit}
                    disabled={isAiLoading || !aiQuery.trim()}
                    className="px-6 py-2.5 bg-primary-blue hover:bg-hover-blue text-white text-sm rounded-xl font-semibold transition-all duration-150 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isAiLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Генерація...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4 mr-2" />
                        Створити
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        {renderUserBlock()}
      </div>
    </div>
  );
};

export default ConfiguratorToolbar;